import { cardLabel, SITE } from '@cardengine/shared';
import { useEffect, useState } from 'react';
import { Celebration } from '../components/Celebration.js';
import { Chat } from '../components/Chat.js';
import { GameLog } from '../components/GameLog.js';
import { Table } from '../components/Table.js';
import { linkProps } from '../router.js';
import { useStore } from '../store.js';

function GameOver() {
  const game = useStore((s) => s.game);
  const lobby = useStore((s) => s.lobby);
  const session = useStore((s) => s.session);
  const rematch = useStore((s) => s.rematch);
  const playAgain = useStore((s) => s.playAgain);
  const leaveLobby = useStore((s) => s.leaveLobby);

  if (!game || game.phase !== 'finished') return null;

  const ranked = game.finishedOrder
    .map((id) => game.players.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const isHost = lobby?.hostId === session?.playerId;
  const youWon = game.finishedOrder[0] === game.yourPlayerId;

  const winsFor = (playerId: string) => lobby?.members.find((m) => m.id === playerId)?.wins ?? 0;
  // The header row is decorative to a screen reader, so each cell says what
  // its number means.
  const winsLabel = (wins: number) => `${wins} ${wins === 1 ? 'win' : 'wins'}`;
  const rounds = lobby?.roundsPlayed ?? 1;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Results">
      <div className="modal__panel modal__panel--wide">
        <header className="modal__head">
          <h2 className="modal__title">{youWon ? 'You win! 🎉' : `${ranked[0]?.name} wins`}</h2>
          <span className="modal__round">Round {rounds}</span>
        </header>

        <ol className="results results--with-wins">
          <li className="results__row results__row--head" aria-hidden="true">
            <span className="results__place" />
            <span className="results__name">Player</span>
            <span className="results__cards">Left in hand</span>
            <span className="results__wins">Wins</span>
            <span className="results__score">Score</span>
          </li>
          {ranked.map((player, index) => (
            <li key={player.id} className={`results__row ${index === 0 ? 'results__row--winner' : ''}`}>
              <span className="results__place">{index + 1}</span>
              <span className="results__name">
                {player.name}
                {player.id === game.yourPlayerId && <span className="seats__tag">you</span>}
              </span>
              <span className="results__cards">
                {(player.finalHand ?? []).map((card) => cardLabel(card)).join(' ') || '—'}
              </span>
              <span className="results__wins" aria-label={winsLabel(winsFor(player.id))}>
                {winsFor(player.id)}
              </span>
              <span className="results__score">{player.finalScore ?? 0}</span>
            </li>
          ))}
        </ol>
        <p className="modal__hint">
          Score is the value left in hand — lower is better. Wins are counted across every round at
          this table.
        </p>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => void leaveLobby()}>
            Leave
          </button>
          {isHost ? (
            <div className="modal__actions-group">
              <button type="button" className="btn btn--secondary" onClick={rematch}>
                Back to lobby
              </button>
              <button type="button" className="btn btn--primary" onClick={playAgain} autoFocus>
                Play again
              </button>
            </div>
          ) : (
            <p className="modal__hint">Waiting for the host to deal again…</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Let the winning card land, celebrate, then show the numbers. */
const LANDING_MS = 650;
const CELEBRATION_MS = 2_300;

export function GameRoom() {
  const lobby = useStore((s) => s.lobby);
  const game = useStore((s) => s.game);
  const leaveLobby = useStore((s) => s.leaveLobby);

  // Open by default where there is room for it; on a phone the table comes first.
  const [panelOpen, setPanelOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 900,
  );
  const [tab, setTab] = useState<'log' | 'chat'>('log');
  const setLogPanelOpen = useStore((s) => s.setLogPanelOpen);

  // The store needs to know, so narration doesn't double up as toasts.
  useEffect(() => {
    setLogPanelOpen(panelOpen && tab === 'log');
    return () => setLogPanelOpen(false);
  }, [panelOpen, tab, setLogPanelOpen]);

  // 'playing' → brief pause → 'celebrating' → 'results'. Slamming the
  // scoreboard up the instant the last card lands gives no moment to enjoy it.
  const finished = game?.phase === 'finished';
  const [endStage, setEndStage] = useState<'none' | 'celebrating' | 'results'>('none');

  useEffect(() => {
    if (!finished) {
      setEndStage('none');
      return;
    }
    const toCelebration = setTimeout(() => setEndStage('celebrating'), LANDING_MS);
    const toResults = setTimeout(() => setEndStage('results'), LANDING_MS + CELEBRATION_MS);
    return () => {
      clearTimeout(toCelebration);
      clearTimeout(toResults);
    };
  }, [finished]);

  if (!lobby || !game) {
    return (
      <main className="game">
        <p className="game__loading">Dealing…</p>
      </main>
    );
  }

  return (
    <main className="game">
      <header className="game__header">
        {/* The way back to the site without giving up your seat. */}
        <a className="game__brand" {...linkProps('/')} title={`${SITE.name} home`}>
          <span className="game__brand-mark" aria-hidden="true">
            ♠
          </span>
          <span className="game__brand-name">{SITE.name}</span>
        </a>
        <span className="game__code">Table {lobby.code}</span>
        <span className="game__turn">Turn {game.turnNumber}</span>
        <div className="game__header-actions">
          <button
            type="button"
            className={`btn btn--ghost btn--small ${panelOpen ? 'is-active' : ''}`}
            onClick={() => setPanelOpen((open) => !open)}
            aria-expanded={panelOpen}
          >
            {panelOpen ? 'Hide log' : 'Show log'}
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={() => void leaveLobby()}>
            Leave
          </button>
        </div>
      </header>

      <div className={`game__body ${panelOpen ? 'game__body--with-chat' : ''}`}>
        <Table />
        {panelOpen && (
          <aside className="game__chat">
            <div className="panel">
              <div className="panel__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'log'}
                  className={`panel__tab ${tab === 'log' ? 'is-active' : ''}`}
                  onClick={() => setTab('log')}
                >
                  Game log
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'chat'}
                  className={`panel__tab ${tab === 'chat' ? 'is-active' : ''}`}
                  onClick={() => setTab('chat')}
                >
                  Chat
                </button>
              </div>
              <div className="panel__body">{tab === 'log' ? <GameLog /> : <Chat />}</div>
            </div>
          </aside>
        )}
      </div>

      {endStage === 'celebrating' && (
        <Celebration
          winnerName={game.players.find((p) => p.id === game.finishedOrder[0])?.name ?? 'Someone'}
          isYou={game.finishedOrder[0] === game.yourPlayerId}
        />
      )}
      {endStage === 'results' && <GameOver />}
    </main>
  );
}
